import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { AgentResponse, CreateOrUpdateAgentRequest } from "./agentsApi";
import { deleteAgent, getAgent, updateAgent } from "./agentsApi";

export const useAgent = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["agents", agentId],
    queryFn: ({ signal }) => getAgent(agentId!, signal),
  });

  const updateAgentMutation = useMutation<AgentResponse, unknown, { id: string; request: CreateOrUpdateAgentRequest }>({
    mutationFn: ({ id, request }) => updateAgent(id, request),
    onSuccess: async (r) => {
      enqueueSnackbar(`Przedstawiciel ${r.name} został zaktualizowany`, { variant: "success" });
      queryClient.setQueryData(["agents", agentId], r);
      await queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować przedstawiciela. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteAgentMutation = useMutation<void, unknown, void>({
    mutationFn: () => deleteAgent(agentId!),
    onSuccess: async () => {
      enqueueSnackbar("Przedstawiciel został usunięty", { variant: "success" });
      queryClient.removeQueries({ queryKey: ["agents", agentId] });
      await queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć przedstawiciela. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    agent: data,
    isLoading,
    updateAgent: (id: string, request: CreateOrUpdateAgentRequest) => updateAgentMutation.mutateAsync({ id, request }),
    deleteAgent: () => deleteAgentMutation.mutateAsync(),
  };
};
